import React, { useState, useMemo } from 'react';

import { useData, useMe, useApp } from './provider';
import { Page, Column, Row, Heading, Copy, Card, Metric, Empty, Button, FormSheet } from './components';
import { trackRecord, newId, type Command, hasPlanTools } from '@ventureroom/core';

export function DashboardScreen() {
  const data = useData();
  const me = useMe();
  
  const { act, navigate } = useApp();
  const record = trackRecord(data, me.id);
  
  const isOwner = me.role === 'Business Owner' || me.role === 'Both' || !me.role;
  const isSkill = me.role === 'Skill Provider' || me.role === 'Both' || !me.role;

  const [logging, setLogging] = useState(false);

  const ownTasks = useMemo(() => {
    return [...data.commitments]
      .filter(c => c.owner === me.id)
      .sort((a, b) => {
        const order = { 'In Progress': 0, 'Completed': 1, 'Cancelled': 2 };
        const aOrder = order[a.status as keyof typeof order] ?? 5;
        const bOrder = order[b.status as keyof typeof order] ?? 5;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.id.localeCompare(b.id);
      });
  }, [data.commitments, me.id]);

  const teamTasks = useMemo(() => {
    const ventureIds = record.ventures.map(v => v.id);
    return [...data.commitments]
      .filter(c => ventureIds.includes(c.ventureId) && c.owner !== me.id)
      .sort((a, b) => {
        const order = { 'In Progress': 0, 'Completed': 1, 'Cancelled': 2 };
        const aOrder = order[a.status as keyof typeof order] ?? 5;
        const bOrder = order[b.status as keyof typeof order] ?? 5;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.id.localeCompare(b.id);
      });
  }, [data.commitments, record.ventures, me.id]);

  const inProgress = ownTasks.filter(c => c.status === 'In Progress').length;
  const completed = ownTasks.filter(c => c.status === 'Completed').length;
  const cancelled = ownTasks.filter(c => c.status === 'Cancelled').length;

  return (
    <Page title={isSkill && !isOwner ? 'Progression' : 'Track Record'}>
      <Column gap={24}>
        {hasPlanTools(me) && (
          <Row>
            <Button onPress={() => navigate('tools')} color="#b91c1c">
              Premium Features
            </Button>
          </Row>
        )}
        
        {isSkill && (
          <Column gap={12}>
            <Row between>
              <Heading size={20}>Freelancer Tasks</Heading>
              {record.ventures.length > 0 && (
                <Button compact onPress={() => setLogging(true)}>+ Log Task</Button>
              )}
            </Row>
            <Row wrap>
              <Card style={{ flex: 1, minWidth: 140 }}>
                <Metric value={completed} label="tasks completed" />
              </Card>
              <Card style={{ flex: 1, minWidth: 140 }}>
                <Metric value={cancelled} label="tasks cancelled" />
              </Card>
              <Card style={{ flex: 1, minWidth: 140 }}>
                <Metric value={inProgress} label="tasks in progress" />
              </Card>
              <Card style={{ flex: 1, minWidth: 140 }}>
                <Metric value={record.endorsements.length} label="endorsements received" />
              </Card>
            </Row>
          </Column>
        )}

        {isOwner && (
          <Column gap={12}>
            <Heading size={20}>Business Milestones</Heading>
            <Row wrap>
              <Card style={{ flex: 1, minWidth: 140 }}>
                <Metric value={record.ventures.length} label="active ventures" />
              </Card>
              <Card style={{ flex: 1, minWidth: 140 }}>
                <Metric value={record.expenseMinor ? `£${(record.expenseMinor / 100).toFixed(0)}` : '£0'} label="capital deployed" />
              </Card>
              <Card style={{ flex: 1, minWidth: 140 }}>
                <Metric value={record.endorsements.length} label="endorsements received" />
              </Card>
            </Row>
          </Column>
        )}

        {isOwner && teamTasks.length > 0 && (
          <Column gap={12}>
            <Heading size={20}>Team Tasks</Heading>
            <Column gap={12}>
              {teamTasks.map(task => {
                const venture = data.ventures.find(v => v.id === task.ventureId);
                const owner = data.profiles.find(p => p.id === task.owner);
                return (
                  <Card key={task.id}>
                    <Row between>
                      <Column gap={4}>
                        <Heading size={18}>{task.title}</Heading>
                        <Copy size={14} muted>{venture?.name} • {owner?.name} • {task.status}</Copy>
                      </Column>
                      {task.status !== 'Completed' && task.status !== 'Cancelled' && (
                        <Button 
                          compact 
                          kind="danger"
                          onPress={() => act({ type: 'commitmentStatus', id: task.id, status: 'Cancelled' } as Command, 'Task cancelled')}
                        >
                          Cancel
                        </Button>
                      )}
                      {task.status === 'Cancelled' && (
                        <Button 
                          compact 
                          kind="secondary"
                          color="#007AFF"
                          onPress={() => act({ type: 'commitmentStatus', id: task.id, status: 'In Progress' } as Command, 'Task restored')}
                        >
                          Undo
                        </Button>
                      )}
                    </Row>
                  </Card>
                );
              })}
            </Column>
          </Column>
        )}

        {isSkill && (
          <Column gap={12}>
            <Heading size={20}>Task Log</Heading>
            {ownTasks.length === 0 ? (
              <Empty title="No tasks logged" detail="Log what you are working on to build your reputation." />
            ) : (
              <Column gap={12}>
                {ownTasks.map(task => {
                  const venture = data.ventures.find(v => v.id === task.ventureId);
                  return (
                    <Card key={task.id}>
                      <Row between>
                        <Column gap={4}>
                          <Heading size={18}>{task.title}</Heading>
                          <Copy size={14} muted>{venture?.name} • {task.status}</Copy>
                        </Column>
                        {task.status !== 'Completed' && task.status !== 'Cancelled' && (
                          <Button 
                            compact 
                            onPress={() => act({ type: 'commitmentStatus', id: task.id, status: 'Completed' } as Command, 'Task completed!')}
                          >
                            Finish
                          </Button>
                        )}
                      </Row>
                    </Card>
                  );
                })}
              </Column>
            )}
          </Column>
        )}

      </Column>

      {logging && (
        <FormSheet
          title="Log a Task"
          fields={[
            { key: 'companyName', label: 'Company', type: 'select', options: record.ventures.map(v => v.name), required: true },
            { key: 'title', label: 'What are you working on?', type: 'long', required: true },
            { key: 'status', label: 'Status', type: 'select', options: ['In Progress', 'Completed', 'Cancelled'], required: true }
          ]}
          initial={{ companyName: record.ventures[0]?.name || '', title: '', status: 'In Progress' }}
          onClose={() => setLogging(false)}
          onSave={async (v) => {
            const venture = record.ventures.find(ven => ven.name === v.companyName);
            if (!venture) return false;
            await act({
              type: 'commitment',
              commitment: {
                id: newId('cmt'),
                ventureId: venture.id,
                title: String(v.title),
                description: '',
                owner: me.id,
                dueDate: new Date().toISOString().slice(0, 10),
                priority: 'Medium',
                status: (v.status as "In Progress" | "Completed" | "Cancelled") || 'In Progress',
                effort: 0,
                milestone: 'General',
                comments: [],
                createdAt: new Date().toISOString()
              }
            } as Command, 'Task logged!');
            setLogging(false);
            return true;
          }}
        />
      )}
    </Page>
  );
}
